import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { Section, Eyebrow, FadeIn, PageHero } from '../components/ui-bits';
import Breadcrumb from '../components/Breadcrumb';
import { Building, Compass, Target } from 'lucide-react';

const QuienesSomos = () => {
  const { t } = useLanguage();

  const principles = [
    { icon: Compass, title: t.about.p1Title, body: t.about.p1Body },
    { icon: Target, title: t.about.p2Title, body: t.about.p2Body },
    { icon: Building, title: t.about.p3Title, body: t.about.p3Body },
  ];

  return (
    <div data-testid="about-page">
      <Breadcrumb items={[{ to: null, label: t.nav.about }]} />
      <PageHero eyebrow={t.about.eyebrow} title={t.about.title} intro={t.about.subtitle} />

      <Section className="pb-24" dataTestId="about-founded">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FadeIn className="lg:col-span-5">
            <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-10 h-full">
              <p className="text-sm uppercase tracking-[0.2em] text-amber-300/80">{t.about.foundedTitle}</p>
              <div className="mt-6 font-display text-7xl md:text-8xl font-extrabold text-white tracking-tight">
                {t.about.foundedYear}
              </div>
              <p className="mt-6 font-body text-white/60 leading-relaxed">{t.about.foundedBody}</p>
            </div>
          </FadeIn>

          <FadeIn delay={0.12} className="lg:col-span-7">
            <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-10 h-full">
              <p className="text-sm uppercase tracking-[0.2em] text-amber-300/80">{t.about.meaningTitle}</p>
              <h3 className="mt-6 font-display text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">
                "{t.home.meaningTranslation.replace(/"/g, '')}"
              </h3>
              <p className="mt-6 font-body text-white/65 leading-relaxed text-lg">{t.about.meaningBody}</p>
            </div>
          </FadeIn>
        </div>
      </Section>

      <Section className="pb-24" dataTestId="about-ownership">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FadeIn className="lg:col-span-7">
            <Eyebrow>{t.about.ownershipTitle}</Eyebrow>
            <h3 className="mt-6 font-display text-2xl md:text-3xl font-bold text-white tracking-tight">
              {t.about.ownershipBody}
            </h3>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[t.about.owner1, t.about.owner2].map((o, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-white/10 bg-white/[0.025] p-6 card-hover"
                  data-testid={`owner-${i + 1}`}
                >
                  <div className="text-xs uppercase tracking-[0.18em] text-amber-300/70">0{i + 1}</div>
                  <div className="mt-3 font-display font-semibold text-white text-lg leading-snug">{o}</div>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.12} className="lg:col-span-5">
            <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-10 h-full">
              <p className="text-sm uppercase tracking-[0.2em] text-amber-300/80">{t.about.leadershipTitle}</p>
              <h3 className="mt-6 font-display text-3xl md:text-4xl font-bold text-white tracking-tight">
                {t.about.leadershipName}
              </h3>
              <p className="mt-3 font-body text-white/55 text-sm">{t.about.leadershipRole}</p>
            </div>
          </FadeIn>
        </div>
      </Section>

      <Section className="pb-32" dataTestId="about-principles">
        <FadeIn>
          <Eyebrow>{t.about.principlesTitle}</Eyebrow>
        </FadeIn>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {principles.map((p, i) => {
            const Icon = p.icon;
            return (
              <FadeIn key={i} delay={i * 0.08}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover" data-testid={`principle-${i + 1}`}>
                  <div className="w-11 h-11 rounded-xl bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-300">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h4 className="mt-6 font-display font-bold text-xl text-white">{p.title}</h4>
                  <p className="mt-3 font-body text-white/55 leading-relaxed">{p.body}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </Section>
    </div>
  );
};

export default QuienesSomos;
